<script setup lang="ts">
import { useQuery } from 'vue-query';
import { useEvent } from '~/composables/api/useEvent';
import { useSystem } from '~/composables/api/useSystem';
import { capitalize } from '~/utils/str'

const { getEventId, setEventId, clearEventId } = useContext();
const { getAllMy } = useEvent();
const { getPrimaryEvent } = useSystem();
const user = useAuthUser();
const isAdmin = computed(() => useAdmin());

const NO_EVENT_TEXT = 'No events available';
const selectedEvent = ref(NO_EVENT_TEXT)

interface Event {
  id: string;
  label?: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
}

const { data: events } = useQuery<Event[]>({
  queryKey: ['myEvents'],
  queryFn: async () => {
    const events = (await getAllMy()) as unknown as Event[];
    const hydratedId = await ensureEventContext(events as any)
    if (hydratedId && hydratedId !== selectedEvent.value) {
      selectedEvent.value = hydratedId
    }
    return events;
  }
})

const { data: primaryEventData } = useQuery({
  queryKey: ['systemPrimaryEvent'],
  queryFn: getPrimaryEvent,
})

const isPrimaryEvent = computed(() =>
  !!primaryEventData.value?.eventId && primaryEventData.value.eventId === selectedEvent.value
)

onMounted(() => {
  if (user && user.id) {
    const savedEventId = getEventId()
    selectedEvent.value = savedEventId || getLatestEvent()?.id || NO_EVENT_TEXT
  }
})

watch(selectedEvent, (newEvent) => {
  if (newEvent && newEvent !== NO_EVENT_TEXT) {
    setEventId(newEvent)
  } else {
    clearEventId()
  }
})

watch(events, (newEvents) => {
  if (newEvents && newEvents.length > 0 && !selectedEvent.value && user!.id) {
    const savedEventId = getEventId()
    if (savedEventId) {
      selectedEvent.value = savedEventId
    }
    
    selectedEvent.value = sortedEvents(newEvents)[0].id
  }
}, { immediate: true })

// Get the short name and full name for backup for display when selected
const selectedEventDisplay = computed(() => {
  const event = events.value?.find(e => e.id === selectedEvent.value)
  return event?.label || event?.name;
})

const sortedEvents = (events: any) => {
  if (!events.length) return []
  return events.sort((a: Event, b: Event) => 
    new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  )
}

const getLatestEvent = (data?: any) => {
  if (data) {
    return sortedEvents(data)[0] || null;
  } else {
    return sortedEvents(events)[0] || null;
  }
}

</script>

<template>
  <!-- Event Selector -->
  <div v-if="isAdmin" class="formkit-outer context-selector-outer w-[125px] m-auto">
    <div class="lg-max:hidden h-[36px] formkit-inner !shadow-none !overflow-hidden !border-t-border border !rounded-xl !text-t-text hover:context-border transition-colors">
      <select
        id="event-selector"
        name="event-selector"
        v-model="selectedEvent"
        class="context-selector-input w-[125px] formkit-input !placeholder-t-text-light !text-sm !pl-3 !pr-9 !font-sans cursor-pointer"
        :class="selectedEvent ? '!text-transparent' : '!text-t-text'"
      >
      <option v-if="!events?.length" value="No events available" disabled>No events available</option>
      <option v-else value="" disabled>Select Event</option>
      <option 
          v-for="event in events"
          :key="event.id"
          :value="event.id"
        >
        {{ event.name }}
      </option>
      </select>
      <span class="formkit-select-icon formkit-icon pointer-events-none absolute right-0" style="top: 14px">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 7" style="margin-right: 8px">
          <path d="M8,6.5c-.13,0-.26-.05-.35-.15L3.15,1.85c-.2-.2-.2-.51,0-.71,.2-.2,.51-.2,.71,0l4.15,4.15L12.15,1.15c.2-.2,.51-.2,.71,0,.2,.2,.2,.51,0,.71l-4.5,4.5c-.1,.1-.23,.15-.35,.15Z" fill="currentColor"></path>
        </svg>
      </span>
    
      <!-- Custom display for selected value -->
      <div v-if="selectedEvent" class="absolute inset-y-0 left-0 right-0 pointer-events-none pl-3 pr-8 flex items-center gap-1 text-sm text-t-text z-10 overflow-hidden">
        <Icon v-if="isPrimaryEvent" name="ri:star-fill" class="w-3.5 h-3.5 text-gray-600 dark:text-white shrink-0" />
        <span class="text-t-text font-medium truncate min-w-0">{{ capitalize(selectedEventDisplay || selectedEvent) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@media (min-width: 2045px) {
  .context-selector-outer {
    width: 180px;
  }
  .context-selector-input {
    width: 180px;
  }
}
</style>
